import { AgGridReact } from 'ag-grid-react';
import { FC, useRef, useState } from 'react';
import Table from "../../../components/Table/Table";
import { RawProd } from "../../../services/rawProd/rawProd.types";
import Button from "../../../components/Button/Button";
import { Variant } from "../../../components/Button/Button.types";
import { RowSelectedEvent } from "ag-grid-community";
import RawProductSelectTable from "../../../components/RawProduct/RawProductSelectTable";
import { ChangeOptionRecipeRawProductsStep, ChangeRawProductOperation } from "./ChangeOptionRecipeRawProducts";
import { ButtonRow, Row } from "./ChangeRawProducts.styles";
import { EditCell } from "../../../components/Table/EditCellRenderer/EditCellRenderer.styles";
import { DeleteButton } from "../../Menus/ChangeSpecs/SelectSpec/SelectSpec.styles";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import theme from "../../../theme";

interface ChangeOptionRecipeRawProductsSelectRawProductsProps {
  changeRawProductOperations: ChangeRawProductOperation[]
  setChangeRawProductOperations: (set: ChangeRawProductOperation[]) => void;
  setStep: (step: ChangeOptionRecipeRawProductsStep) => void;
}

const SelectRawProducts: FC<ChangeOptionRecipeRawProductsSelectRawProductsProps> = (
    {
      changeRawProductOperations,
      setChangeRawProductOperations,
      setStep
    }
) => {
  const currentRawProductGridRef = useRef<AgGridReact>();
  const desiredRawProductGridRef = useRef<AgGridReact>();
  const operationsGridRef = useRef<AgGridReact>();

  const [selectedCurrentRawProduct, setSelectedCurrentRawProduct] = useState<RawProd | undefined>();

  const selectCurrentRawProduct = (
      event: RowSelectedEvent
  ) => {
    setSelectedCurrentRawProduct(event.api.getSelectedRows()?.[0]);
  };

  const [selectedDesiredRawProduct, setSelectedDesiredRawProduct] = useState<RawProd | undefined>();

  const selectDesiredRawProduct = (
      event: RowSelectedEvent
  ) => {
    setSelectedDesiredRawProduct(event.api.getSelectedRows()?.[0]);
  };

  const addChangeRawProductOperation = () => {
    if (selectedCurrentRawProduct === undefined || selectedDesiredRawProduct === undefined) {
      return;
    }
    setChangeRawProductOperations([
      ...changeRawProductOperations,
      {
        currentRawProduct: selectedCurrentRawProduct,
        desiredRawProduct: selectedDesiredRawProduct
      }
    ])
    currentRawProductGridRef.current?.api.deselectAll()
    desiredRawProductGridRef.current?.api.deselectAll()
  }

  const renderRemoveIcon = (params: any) => {
    return (
        <EditCell>
          <DeleteButton>
            <DeleteOutlinedIcon
                fontSize='inherit'
                htmlColor={theme.colors.red}
                style={{ cursor: 'pointer', height: '50%' }}
                onClick={() => removeChangeRawProductOperation(params.data)}
            />
          </DeleteButton>
        </EditCell>
    );
  };

  const removeChangeRawProductOperation = (
      remove: ChangeRawProductOperation
  ) => {
    setChangeRawProductOperations(
        changeRawProductOperations.filter(operation => operation !== remove)
    )
  }

  return (
      <>
        <Row>
          <RawProductSelectTable
              ref={currentRawProductGridRef}
              title={"Current Raw Product"}
              onSelected={selectCurrentRawProduct}
          />
          <RawProductSelectTable
              ref={desiredRawProductGridRef}
              title={"Desired Raw Product"}
              onSelected={selectDesiredRawProduct}
          />
        </Row>
        <ButtonRow>
          <Button
              label={'Add Operation'}
              variant={Variant.Primary}
              onClick={addChangeRawProductOperation}
              disabled={selectedCurrentRawProduct === undefined || selectedDesiredRawProduct === undefined}
          />
        </ButtonRow>
        <Table
            ref={operationsGridRef}
            showHeader={true}
            headerTitle="Change Raw Product Operations"
            rowData={changeRawProductOperations}
            returnedRecords={changeRawProductOperations.length}
            columnDefs={[
              {
                minWidth: 80,
                flex: 1,
                field: 'currentRawProduct.moaRawProdNo',
                headerName: 'Current Raw Product'
              },
              {
                minWidth: 80,
                flex: 2,
                field: 'currentRawProduct.moaRawProdDesc',
                headerName: 'Description'
              },
              {
                width: 50,
                valueGetter: () => '→',
                floatingFilter: false,
                sortable: false
              },
              {
                minWidth: 80,
                flex: 1,
                field: 'desiredRawProduct.moaRawProdNo',
                headerName: 'Desired Raw Product'
              },
              {
                minWidth: 80,
                flex: 2,
                field: 'desiredRawProduct.moaRawProdDesc',
                headerName: 'Description'
              },
              {
                width: 50,
                cellRenderer: renderRemoveIcon,
                resizable: false,
                floatingFilter: false,
                sortable: false
              }
            ]}
        />
        <ButtonRow>
          <Button
              label={'Next'}
              onClick={() => setStep(ChangeOptionRecipeRawProductsStep.SelectSpecs)}
              disabled={changeRawProductOperations.length === 0}
          />
        </ButtonRow>
      </>
  );
};

export default SelectRawProducts;